/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { FC, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { createVideo, createVideoVariables } from '__generatedTypes__/createVideo'
import { updateVideoCategory, updateVideoCategoryVariables } from '__generatedTypes__/updateVideoCategory'
import { CREATE_VIDEO, UPDATE_VIDEO_CATEGORY } from 'queries/video'
import { useHistory } from 'hooks/useHistory'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { IQuizData, IVideoCategoryProps } from '../types'
import withVideoCategoryCheck from '../withVideoCategoryCheck'
import * as commonClasses from '../commonStyles'
import VideoQuizForm from './VideoQuizForm'

const VideoResourceCreate: FC<IVideoCategoryProps> = ({ videoCategory, redirectToVideoDashboard }) => {
  const [createVideo] = useMutation<createVideo, createVideoVariables>(CREATE_VIDEO)
  const [updateVideoCategory] = useMutation<updateVideoCategory, updateVideoCategoryVariables>(UPDATE_VIDEO_CATEGORY)
  const { populateHistoryWithUser } = useHistory()
  const { platform = '' } = useParams()
  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()

  const handleSubmit = useCallback(
    async (fieldsValues: IQuizData) => {
      try {
        const { data } = await createVideo({
          variables: {
            createVideoInput: {
              order: videoCategory.video.length,
              questionIds: fieldsValues.questions?.map(({ id }) => id),
              questions: fieldsValues.questions?.map(({ id }) => ({ id })),
              title: fieldsValues.title,
              history: populateHistoryWithUser(),
            },
          },
        })

        setSubmitQuestionError(null)

        const videos = videoCategory.video?.map((video) => ({ id: video.id })) || []
        await updateVideoCategory({
          variables: {
            updateVideoCategoryInputFieldName: {
              id: videoCategory.id,
              video: [...videos, { id: data?.createVideo.id || '' }],
            },
          },
        })

        redirectToVideoDashboard()
        toast.success('Video quiz created successfully')
      } catch (error) {
        toast.error('Failed to create video quiz')
        handleServerError(error)
      }
    },
    [platform, videoCategory]
  )

  return (
    <div css={commonClasses.containerPadding}>
      <VideoQuizForm
        platformSlug={platform as PlatformSlug}
        videoCategory={videoCategory}
        errorText={submitQuestionError}
        handleSubmit={handleSubmit}
      />
    </div>
  )
}

export default withVideoCategoryCheck(VideoResourceCreate)
